import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLoader } from '../../../../context/LoaderContext';
import { useDebounce } from '../../../../hooks';
import { StatusBar } from './StatusBar';
import { DropDown, TitleBar } from '../../../common';
import { BatchDetails } from './BatchDetails';
import { BatchEditBox } from './BatchEditBox';
import { TITLES, ROUTES, POPUP_TYPE, DROPDOWN, BUTTON, EDIT_BOX_STATE } from '../../../../constants';
import { batchInfo, productImage, updatePriority } from '../../../../service/manufacturing';
import { popup, batchDataCompiler, getLocalStorageItem } from '../../../../utils';
import { Button, PopUpBox } from '../../../shared';
import { IN_PROGRESS_LOT_IDS_KEY, calculateTotalSewDamagedQty, getAvailableSewCapacity } from './utils/batchHelpers';


export const LotInfo = () => {
  const [editBoxState, setEditBoxState] = useState(EDIT_BOX_STATE.CLOSED);
  const [selectedBatch, setSelectedBatch] = useState('');
  const [reportType, setReportType] = useState('cutting'); // 'cutting' or 'sewing'
  const navigate = useNavigate();
  const { state } = useLocation();
  const [reload, setReload] = useState(true);
  const [lot, setLot] = useState({});
  const [field, setField] = useState('');
  const [openPopUpBox, setOpenPopBox] = useState(false);
  const [image, setImage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);

  // Single source of truth for sew capacity calculation
  const availableSewCapacity = useMemo(() => {
    const totalSewDamagedQty = calculateTotalSewDamagedQty(lot?.batch);
    return getAvailableSewCapacity(lot, totalSewDamagedQty);
  }, [lot]);

  // API Call
  const getLotDetails = useDebounce(() => {
    batchInfo({
      data: {
        order_line_id: state?.id,
      },
    })
      .then((response) => {
        setIsLoading(false);
        if (Object.keys(response.data).length) {
          const compiledData = batchDataCompiler(response.data);
          setLot(compiledData);
        } else {
          popup(POPUP_TYPE.ERR, response.message);
        }
      })
      .catch(() => setIsLoading(false));
    setReload(false);
  }, 400);

  useEffect(() => {
  const productId =
    typeof state?.product_id === "object"
      ? state?.product_id?.id
      : state?.product_id;

  if (productId && !image) {
    productImage({ data: { product_id: productId } })
      .then((response) => {
        if (!response) return;
        if (response.data.length) {
          setImage(response?.data?.[0]?.image_1920);
        }
      });
  }
}, [state, image]);


  useEffect(() => {
    if (!state) navigate(ROUTES.MANUFACTURING);

    if (reload) {
      setIsLoading(true);
      getLotDetails();
    }
  }, [reload]);

  useEffect(() => {
    const lotId = lot?.lot_no;
  
    const checkInProgress = () => {
      const inProgressIds = getLocalStorageItem(IN_PROGRESS_LOT_IDS_KEY) || [];
      const isLoading = lotId && inProgressIds.includes(lotId);
      setIsSaveLoading(isLoading);
  
      if (!isLoading) {
        getLotDetails();
      }
    };
  
    checkInProgress();
  
    window.addEventListener('__inProgressLotUpdated', checkInProgress);
  
    return () => {
      window.removeEventListener('__inProgressLotUpdated', checkInProgress);
    };
  }, [lot?.lot_no]);
  
  return (
    <>
      <TitleBar
        title={[
          { text: TITLES.MANUFACTURING, route: ROUTES.MANUFACTURING },
          { text: lot?.lot_no, route: ROUTES.NULL },
        ]}
      />
      <div className="w-100 bg-white border-def-up">
        <StatusBar lot={lot} setOpenPopBox={setOpenPopBox} />
        <div className="secondary-scroll-bar">
          <div className="d-flex flex-column ms-2">
            <div className="d-flex">
              <h5 className="mt-1">Product : </h5>
              <h6 className="ms-3 mt-2">{lot?.lotData?.product_id?.display_name || 'N/A'}</h6>
            </div>
            <div className="d-flex">
              <h5 className="mt-1">Finished Product :</h5>
              <h6 className="ms-3 mt-2">{lot?.lotData?.dyeing_product_id?.display_name || 'N/A'}</h6>
            </div>
          </div>
          {isSaveLoading ? (
            <div className="text-center my-5">
              <span className="spinner-border spinner-border-sm text-primary" role="status" />
              <span className="ms-2">Loading...</span>
            </div>
          ) : <><BatchDetails
            setSelectedBatch={setSelectedBatch}
            lot={lot}
            editBoxState={editBoxState}
            setEditBoxState={setEditBoxState}
            setField={setField}
            reload={reload}
            setReload={setReload}
            image={image}
            isLoading={isLoading}
            setReportType={setReportType}
            availableSewCapacity={availableSewCapacity}
          />
            <BatchEditBox
              selectedBatch={selectedBatch}
              lot={lot}
              setLot={setLot}
              editBoxState={editBoxState}
              setEditBoxState={setEditBoxState}
              field={field}
              setField={setField}
              reload={reload}
              setReload={setReload}
              isSaveLoading={isSaveLoading}
              setIsSaveLoading={setIsSaveLoading}
              reportType={reportType}
            /></>}
        </div>
        <PopUpBox openPopUpBox={openPopUpBox}>
          <PopUpBody
            setOpenPopBox={setOpenPopBox}
            lot={lot}
            reload={reload}
            setReload={setReload}
          />
        </PopUpBox>
      </div>
    </>
  );
};

const PopUpBody = ({ setOpenPopBox, lot, reload, setReload }) => {
  const { setLoading } = useLoader();
  const [priority, setPriority] = useState(lot?.priority || 'Select Priority');
  const handleApiCall = useDebounce(() => {
    let newPriority = '';
    if (priority === 'Priority') newPriority = 'priority';
    else if (priority === 'Make First Order') newPriority = 'make_first_order';
    else if (priority === 'Low Priority') newPriority = 'low_priority';
    updatePriority({
      data: { line_id: lot?.lotData?.id, priority: newPriority },
    })
      .then((response) => {
        setLoading(false);
        popup('success', response.message);
        setOpenPopBox(false);
        setReload(!reload);
      })
      .catch(() => {
        setLoading(false);
      });
  }, 400);
  return (
    <>
      <div className="d-flex m-1">
        <div className="w-75 modal-body py-0">
          <h4 className="text-center text-special mb-3">Change Priority</h4>
        </div>
      </div>
      <DropDown dropdownMenu={DROPDOWN.MFG_SORT_1} value={priority} setValue={setPriority} />
      <hr />
      <div className="pb-3 mt-2 d-flex align-items-center justify-content-end mx-4 modal-btn custom-button">
        <div className="ms-5 ps-5 w-50 d-flex align-items-center justify-content-between">
          <Button
            clickHandler={handleApiCall}
            className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
            {BUTTON.UPDATE}
          </Button>
          <Button
            clickHandler={() => setOpenPopBox(false)}
            className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
            {BUTTON.CLOSE}
          </Button>
        </div>
      </div>
    </>
  );
};

export default PopUpBody;
